import { useCrud } from './useCrud';
import { STORAGE_KEYS } from '../models/constants';
import { createDebtAccount, validateDebtAccount } from '../models/DebtAccount';
import { 
  calculateAccountBalance, 
  processPayment, 
  applyAccountAdjustment,
  calculatePaymentAllocation 
} from '../utils/financialCalculations';

/**
 * DebtAccount-specific CRUD hook with localStorage persistence
 */
export const useDebtAccounts = (initialData = []) => {
  // Load initial data from localStorage if available
  const loadInitialData = () => {
    if (initialData.length > 0) return initialData;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.DEBT_ACCOUNTS);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading debt accounts from localStorage:', error);
      return [];
    }
  };

  const debtAccountCrud = useCrud(loadInitialData());

  // Override create to save to localStorage
  const createDebtAccountWithValidation = async (debtAccountData) => {
    const debtAccount = createDebtAccount(debtAccountData);
    const validation = validateDebtAccount(debtAccount);
    
    if (!validation.isValid) {
      return { success: false, errors: validation.errors };
    }

    try {
      // Use the create method from useCrud which properly handles ID generation
      const newDebtAccount = debtAccountCrud.create(debtAccount);
      return { success: true, data: newDebtAccount };
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Override update to save to localStorage
  const updateDebtAccount = async (id, updates) => {
    const existingAccount = debtAccountCrud.getById(id);
    if (!existingAccount) {
      return { success: false, errors: ['Debt account not found'] };
    }

    const updatedAccount = { ...existingAccount, ...updates, updatedAt: new Date() };
    const validation = validateDebtAccount(updatedAccount);
    
    if (!validation.isValid) {
      return { success: false, errors: validation.errors };
    }

    try {
      debtAccountCrud.update(id, updates);
      const updatedItems = debtAccountCrud.items.map(item => 
        item.id === id ? updatedAccount : item
      );
      saveToStorage(updatedItems);
      return { success: true, data: updatedAccount };
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Override remove to save to localStorage
  const removeDebtAccount = (id) => {
    try {
      debtAccountCrud.remove(id);
      saveToStorage(debtAccountCrud.items);
      return { success: true };
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Save to localStorage
  const saveToStorage = (debtAccounts) => {
    try {
      localStorage.setItem(STORAGE_KEYS.DEBT_ACCOUNTS, JSON.stringify(debtAccounts));
    } catch (error) {
      console.error('Error saving debt accounts to localStorage:', error);
    }
  };

  // Record a payment and update balance using financial calculation engine
  const recordPaymentForAccount = async (accountId, paymentAmount, paymentDate = new Date()) => {
    const account = debtAccountCrud.getById(accountId);
    if (!account) {
      return { success: false, errors: ['Debt account not found'] };
    }

    try {
      const updatedAccount = processPayment(account, paymentAmount, paymentDate);
      return await updateDebtAccount(accountId, updatedAccount);
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Apply adjustment to account using financial calculation engine
  const applyAdjustmentToAccount = async (accountId, adjustmentAmount, reason = '') => {
    const account = debtAccountCrud.getById(accountId);
    if (!account) {
      return { success: false, errors: ['Debt account not found'] };
    }

    try {
      const updatedAccount = applyAccountAdjustment(account, adjustmentAmount, reason);
      return await updateDebtAccount(accountId, updatedAccount);
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Calculate payment allocation for an account
  const calculatePaymentAllocationForAccount = (accountId, paymentAmount, allocationRules = {}) => {
    const account = debtAccountCrud.getById(accountId);
    if (!account) {
      return { success: false, errors: ['Debt account not found'] };
    }

    try {
      return calculatePaymentAllocation(account, paymentAmount, allocationRules);
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Get debt accounts by client ID
  const getDebtAccountsByClientId = (clientId) => {
    return debtAccountCrud.items.filter(account => 
      account.clientId == clientId || 
      account.clientId === String(clientId) || 
      String(account.clientId) === String(clientId)
    );
  };

  // Get accounts with outstanding balances
  const getAccountsWithBalance = () => {
    return debtAccountCrud.items.filter(account => account.balance > 0);
  };

  // Get total debt amount across all accounts
  const getTotalDebtAmount = () => {
    return debtAccountCrud.items.reduce((total, account) => total + account.debtAmount, 0);
  };

  // Get total outstanding balance across all accounts
  const getTotalOutstandingBalance = () => {
    return debtAccountCrud.items.reduce((total, account) => total + account.balance, 0);
  };

  // Recalculate balance for an account using financial calculation engine
  const recalculateBalance = async (accountId) => {
    const account = debtAccountCrud.getById(accountId);
    if (!account) {
      return { success: false, errors: ['Debt account not found'] };
    }

    const newBalance = calculateAccountBalance(account);
    return await updateDebtAccount(accountId, { balance: newBalance });
  };

  return {
    ...debtAccountCrud,
    create: createDebtAccountWithValidation,
    update: updateDebtAccount,
    remove: removeDebtAccount,
    recordPaymentForAccount,
    applyAdjustmentToAccount,
    calculatePaymentAllocationForAccount,
    getDebtAccountsByClientId,
    getAccountsWithBalance,
    getTotalDebtAmount,
    getTotalOutstandingBalance,
    recalculateBalance,
    saveToStorage: () => saveToStorage(debtAccountCrud.items) // Explicit save using current state
  };
};